import type { DataBuilderModel } from '../../api/data-builder-api.types';
import {
  type ConditionContext,
  evaluateCondition,
} from '../../api/evaluate-condition';
import type { ModelVersionSelections } from '../../data-builder.store';

export type VersionOption = {
  label: string;
  value: number | string;
};

const PATCH_VERSION_KEYS = new Set([
  'job_taxonomy_patch_version',
  'skill_taxonomy_patch_version',
  'activity_taxonomy_patch_version',
]);

const isPatchVersionKey = (key: string): boolean => PATCH_VERSION_KEYS.has(key);

const compareVersionStrings = (a: string, b: string): number => {
  const partsA = a.split('.').map((s) => parseInt(s, 10) || 0);
  const partsB = b.split('.').map((s) => parseInt(s, 10) || 0);
  const len = Math.max(partsA.length, partsB.length);
  for (let i = 0; i < len; i++) {
    const na = partsA[i] ?? 0;
    const nb = partsB[i] ?? 0;
    if (na !== nb) return na - nb;
  }
  return 0;
};

const toNumericVersionValue = (value: number | string): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const toVersionValue = (
  value: number | string,
  modelKey: string
): number | string | null => {
  if (isPatchVersionKey(modelKey)) {
    return typeof value === 'string' ? value : String(value);
  }

  return toNumericVersionValue(value);
};

const TAXONOMY_VERSION_KEYS = [
  'job_taxonomy_version',
  'skill_taxonomy_version',
  'activity_taxonomy_version',
] as const satisfies readonly (keyof ModelVersionSelections)[];

const TAXONOMY_ID_KEYS = [
  'job_taxonomy_id',
  'skill_taxonomy_id',
  'activity_taxonomy_id',
] as const satisfies readonly (keyof ModelVersionSelections)[];

const versionValuesMatch = (
  a: number | string,
  b: number | string
): boolean => {
  if (a === b) {
    return true;
  }
  if (typeof a === 'number' && typeof b === 'string') {
    const n = Number(b);
    return Number.isFinite(n) && a === n;
  }
  if (typeof a === 'string' && typeof b === 'number') {
    const n = Number(a);
    return Number.isFinite(n) && b === n;
  }
  return false;
};

export function getDefaultVersionValue(
  model: DataBuilderModel
): number | string | undefined {
  const values = model.versions
    .map((version) => toVersionValue(version.value, model.key))
    .filter((value): value is number | string => value !== null);

  if (values.length === 0) {
    return undefined;
  }

  if (isPatchVersionKey(model.key)) {
    const sorted = [...values].sort((a, b) =>
      compareVersionStrings(String(a), String(b))
    );
    return sorted[sorted.length - 1];
  }

  const numericValues = values.filter(
    (v): v is number => typeof v === 'number'
  );

  if (numericValues.length === 0) {
    return undefined;
  }

  if (isTaxonomyIdModelKey(model.key)) {
    return Math.min(...numericValues);
  }

  return Math.max(...numericValues);
}

export function getVersionDisplayLabel(
  model: DataBuilderModel,
  value: number | string
): string {
  for (const version of model.versions) {
    const vv = toVersionValue(version.value, model.key);
    if (vv === null) {
      continue;
    }
    if (versionValuesMatch(vv, value)) {
      return version.name;
    }
  }
  return String(value);
}

const TAXONOMY_VERSION_TO_ID_KEY: Record<string, string> = {
  job_taxonomy_version: 'job_taxonomy_id',
  skill_taxonomy_version: 'skill_taxonomy_id',
  activity_taxonomy_version: 'activity_taxonomy_id',
};

const TAXONOMY_TO_PATCH_VERSION_KEY: Record<string, string> = {
  job_taxonomy_version: 'job_taxonomy_patch_version',
  job_taxonomy_id: 'job_taxonomy_patch_version',
  skill_taxonomy_version: 'skill_taxonomy_patch_version',
  skill_taxonomy_id: 'skill_taxonomy_patch_version',
  activity_taxonomy_version: 'activity_taxonomy_patch_version',
  activity_taxonomy_id: 'activity_taxonomy_patch_version',
};

export const isTaxonomyIdModelKey = (key: string): boolean =>
  TAXONOMY_ID_KEYS.includes(key as (typeof TAXONOMY_ID_KEYS)[number]);

export const isTaxonomyVersionModelKey = (key: string): boolean =>
  TAXONOMY_VERSION_KEYS.includes(key as (typeof TAXONOMY_VERSION_KEYS)[number]);

export const splitModelsByCategory = (models: DataBuilderModel[]) =>
  models.reduce(
    (acc, model) => {
      if (isTaxonomyIdModelKey(model.key)) {
        return {
          ...acc,
          taxonomyIdModels: [...acc.taxonomyIdModels, model],
        };
      }

      if (isTaxonomyVersionModelKey(model.key)) {
        return {
          ...acc,
          modelVersionModels: [...acc.modelVersionModels, model],
        };
      }

      return {
        ...acc,
        modelVersionModels: [...acc.modelVersionModels, model],
      };
    },
    {
      taxonomyIdModels: [] as DataBuilderModel[],
      modelVersionModels: [] as DataBuilderModel[],
    }
  );

export function getDefaultModelVersions(
  models: DataBuilderModel[]
): ModelVersionSelections {
  const out: ModelVersionSelections = {};

  for (const model of models) {
    const key = model.key as keyof ModelVersionSelections;

    if (model.key.endsWith('_taxonomy_id')) {
      const defaultValue = getDefaultVersionValue(model);
      if (defaultValue !== undefined) {
        out[key] = defaultValue;
      }
      continue;
    }

    const defaultValue = getDefaultVersionValue(model);

    if (defaultValue !== undefined) {
      out[key] = defaultValue;
    }
  }

  return out;
}

export function getSelectedModelVersion(
  model: DataBuilderModel,
  modelVersions: ModelVersionSelections
): number | string | undefined {
  const key = model.key as keyof ModelVersionSelections;
  const selected = modelVersions[key];
  if (selected !== undefined) {
    return selected;
  }

  return getDefaultVersionValue(model);
}

export const getVisibleVersionOptions = (
  model: DataBuilderModel,
  modelVersions: ModelVersionSelections,
  settings?: ConditionContext['settings']
): VersionOption[] => {
  const context = {
    models: modelVersions,
    settings,
  };

  return model.versions
    .filter((version) => evaluateCondition(version.condition, context))
    .flatMap((version) => {
      const versionValue = toVersionValue(version.value, model.key);

      if (versionValue === null) {
        return [];
      }

      return [
        {
          label: version.name,
          value: versionValue,
        },
      ];
    });
};

export const getSelectedOption = (
  options: VersionOption[],
  value: number | string | undefined
): VersionOption | null => {
  if (options.length === 0) {
    return null;
  }

  const fallbackOption = (() => {
    const numericOptions = options.filter(
      (opt): opt is VersionOption & { value: number } =>
        typeof opt.value === 'number'
    );
    if (numericOptions.length > 0) {
      return numericOptions.reduce((highest, option) =>
        option.value > highest.value ? option : highest
      );
    }
    return options[options.length - 1] ?? null;
  })();

  if (value === undefined) {
    return fallbackOption;
  }

  return options.find((option) => option.value === value) ?? fallbackOption;
};

export const isLatestVersions = (
  modelVersions: ModelVersionSelections,
  models: DataBuilderModel[]
): boolean => {
  const latestDefaults = getDefaultModelVersions(
    models.filter((model) => !isTaxonomyIdModelKey(model.key))
  );

  return Object.entries(latestDefaults).every(([key, value]) => {
    const selectedValue = modelVersions[key as keyof ModelVersionSelections];

    if (selectedValue === undefined) {
      return true;
    }

    return selectedValue === value;
  });
};

export const getLatestVisibleTaxonomyIdValue = (
  taxonomyIdModel: DataBuilderModel,
  updatedModelVersions: ModelVersionSelections,
  settings?: ConditionContext['settings']
): number | undefined => {
  const options = getVisibleVersionOptions(
    taxonomyIdModel,
    updatedModelVersions,
    settings
  );
  const numericOptions = options
    .map((opt) => (typeof opt.value === 'number' ? opt.value : null))
    .filter((v): v is number => v !== null);

  if (numericOptions.length === 0) {
    return undefined;
  }

  return Math.min(...numericOptions);
};

export const getLatestVisiblePatchVersionValue = (
  patchModel: DataBuilderModel,
  updatedModelVersions: ModelVersionSelections,
  settings?: ConditionContext['settings']
): string | undefined => {
  const options = getVisibleVersionOptions(
    patchModel,
    updatedModelVersions,
    settings
  );
  const stringValues = options.map((opt) => String(opt.value));

  if (stringValues.length === 0) {
    return undefined;
  }

  const sortedValues = [...stringValues].sort(compareVersionStrings);
  return sortedValues[sortedValues.length - 1];
};

export const getDependentTaxonomyIdKey = (
  versionKey: string
): string | undefined => TAXONOMY_VERSION_TO_ID_KEY[versionKey];

export const getDependentPatchVersionKey = (key: string): string | undefined =>
  TAXONOMY_TO_PATCH_VERSION_KEY[key];

export function getVisibleDefaults(
  models: DataBuilderModel[],
  modelVersions: ModelVersionSelections,
  settings?: ConditionContext['settings']
): ModelVersionSelections {
  const out: ModelVersionSelections = {};

  for (const model of models) {
    const key = model.key as keyof ModelVersionSelections;
    const options = getVisibleVersionOptions(model, modelVersions, settings);

    if (options.length === 0) {
      continue;
    }

    const numericValues = options
      .map((opt) => (typeof opt.value === 'number' ? opt.value : null))
      .filter((v): v is number => v !== null);

    if (numericValues.length > 0) {
      out[key] = isTaxonomyIdModelKey(model.key)
        ? Math.min(...numericValues)
        : Math.max(...numericValues);
    } else if (isPatchVersionKey(model.key)) {
      const stringValues = options.map((opt) => String(opt.value));
      const sorted = [...stringValues].sort(compareVersionStrings);
      out[key] = sorted[sorted.length - 1];
    } else {
      const lastOption = options[options.length - 1];
      if (lastOption !== undefined) {
        out[key] = lastOption.value;
      }
    }
  }

  return out;
}
